import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css } from 'styled-components';
import { navLinks, socialMedia } from '@config';
import { loaderDelay } from '@utils';
import { useScrollDirection, usePrefersReducedMotion } from '@hooks';
import { Menu } from '@components';
import { IconLogo, IconGitHub, IconLinkedin, IconTwitter, IconInstagram } from '@components/icons';

const StyledHeader = styled.header`
  ${({ theme }) => theme.mixins.flexCenter};
  position: fixed;
  top: 0;
  z-index: 11;
  width: 100%;
  height: var(--nav-height);
  pointer-events: none;
  transition: var(--transition);

  ${props =>
    props.scrollDirection === 'down' &&
    !props.scrolledToTop &&
    css`
      transform: translateY(-100%);
    `};
`;

const StyledNav = styled.nav`
  ${({ theme }) => theme.mixins.flexBetween};
  position: relative;
  width: auto;
  min-width: 600px;
  max-width: max-content;
  padding: 10px 20px;
  margin-top: 20px;
  background-color: rgba(18, 18, 18, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 100px;
  color: var(--text-main);
  font-family: var(--font-heading);
  z-index: 12;
  pointer-events: auto;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 100px;
    backdrop-filter: blur(20px);
    z-index: -1;
  }

  @media (max-width: 768px) {
    width: 90%;
    min-width: auto;
    margin-top: 10px;
    padding: 10px;
  }

  .logo {
    ${({ theme }) => theme.mixins.flexCenter};
    margin-right: 20px;
    a {
      color: var(--accent-main);
      width: 40px;
      height: 40px;
      &:hover,
      &:focus {
        transform: scale(1.1);
      }
      svg {
        fill: none;
        transition: var(--transition);
        user-select: none;
      }
    }
  }
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    display: none;
  }

  ol {
    display: flex;
    align-items: center;
    gap: 30px;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      font-size: var(--fz-xs);
      font-weight: 600;

      a {
        padding: 5px;
        transition: var(--transition);
        
        &:hover,
        &.active {
          color: var(--accent-main);
        }
      }
    }
  }

  .social-links {
    display: flex;
    align-items: center;
    gap: 25px;
    padding-left: 30px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      color: var(--text-muted);
      transition: var(--transition);

      &:hover,
      &:focus {
        color: var(--accent-main);
        transform: translateY(-2px);
      }

      svg {
        width: 100%;
        height: 100%;
      }
    }
  }
`;

const Nav = ({ isHome }) => {
  const [isMounted, setIsMounted] = useState(!isHome);
  const scrollDirection = useScrollDirection('down');
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 100);

    const handleScroll = () => {
      setScrolledToTop(window.pageYOffset < 50);

      const sections = ['about', 'jobs', 'projects', 'certifications', 'contact'];
      let current = '';
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 300 && rect.bottom >= 300) {
            current = '/#' + section;
            break;
          }
        }
      }
      setActiveSection(prev => (prev !== current ? current : prev));
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const timeout = isHome ? loaderDelay : 0;
  const fadeClass = isHome ? 'fade' : '';
  const fadeDownClass = isHome ? 'fadedown' : '';

  const Logo = (
    <div className="logo" tabIndex="-1">
      {isHome ? (
        <a href="/" aria-label="home">
          <IconLogo />
        </a>
      ) : (
        <Link to="/" aria-label="home">
          <IconLogo />
        </Link>
      )}
    </div>
  );

  return (
    <StyledHeader scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
      <StyledNav>
        {prefersReducedMotion ? (
          <>
            {Logo}

            <StyledLinks>
              <ol>
                {navLinks &&
                  navLinks.map(({ url, name }, i) => (
                    <li key={i}>
                      <Link to={url} className={activeSection === url ? 'active' : ''}>{name}</Link>
                    </li>
                  ))}
              </ol>
              <div className="social-links">
                {socialMedia &&
                  socialMedia.map(({ url, name }, i) => (
                    <a key={i} href={url} aria-label={name} target="_blank" rel="noreferrer">
                      {name === 'GitHub' ? (
                        <IconGitHub />
                      ) : name === 'Linkedin' ? (
                        <IconLinkedin />
                      ) : name === 'Twitter' ? (
                        <IconTwitter />
                      ) : name === 'Instagram' ? (
                        <IconInstagram />
                      ) : null}
                    </a>
                  ))}
              </div>
            </StyledLinks>

            <Menu />
          </>
        ) : (
          <>
            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <>{Logo}</>
                </CSSTransition>
              )}
            </TransitionGroup>

            <StyledLinks>
              <ol>
                <TransitionGroup component={null}>
                  {isMounted &&
                    navLinks &&
                    navLinks.map(({ url, name }, i) => (
                      <CSSTransition key={i} classNames={fadeDownClass} timeout={timeout}>
                        <li key={i} style={{ transitionDelay: `${isHome ? i * 100 : 0}ms` }}>
                          <Link to={url} className={activeSection === url ? 'active' : ''}>{name}</Link>
                        </li>
                      </CSSTransition>
                    ))}
                </TransitionGroup>
              </ol>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                    <div
                      className="social-links"
                      style={{ transitionDelay: `${isHome ? navLinks.length * 100 : 0}ms` }}>
                      {socialMedia &&
                        socialMedia.map(({ url, name }, i) => (
                          <a key={i} href={url} aria-label={name} target="_blank" rel="noreferrer">
                            {name === 'GitHub' ? (
                              <IconGitHub />
                            ) : name === 'Linkedin' ? (
                              <IconLinkedin />
                            ) : name === 'Twitter' ? (
                              <IconTwitter />
                            ) : name === 'Instagram' ? (
                              <IconInstagram />
                            ) : null}
                          </a>
                        ))}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>
            </StyledLinks>

            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <Menu />
                </CSSTransition>
              )}
            </TransitionGroup>
          </>
        )}
      </StyledNav>
    </StyledHeader>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
};

export default Nav;
